import { useI18n } from "@/hooks/useI18n";
import { useMoneyCopy } from "@/lib/i18n/money-copy";
import { formatPlanAmount } from "@/lib/builder/utils/payment-format";

interface PaymentStatusCellProps {
  paidAmount: number;
  /** Rate ancora aperte. In rosso quando almeno una e' gia' scaduta. */
  pendingAmount: number;
  /** Quota di `pendingAmount` gia' scaduta: cambia solo il colore, non i numeri. */
  overdueAmount?: number;
  /** Pagato + in attesa. Arriva dai piani rate, non dal totale dell'itinerario. */
  totalAmount: number;
  /** Nessun piano rate: la terna non esiste, e uno zero si leggerebbe come un debito. */
  empty?: boolean;
  /**
   * Barra piu' corta, allineata a destra sotto il totale. Per le tabelle dove la colonna
   * accanto (lo stato) va su due righe e la barra a tutta cella pesa troppo.
   */
  compact?: boolean;
}

/**
 * Lo stato dei pagamenti di una riga in una cella sola: totale in evidenza, barra di
 * avanzamento, e sotto i due addendi.
 *
 * Erano tre colonne di importi e per capire "a che punto sta" bisognava sottrarre a mente.
 * Qui il totale e' il numero grande perche' e' quello che si cerca per primo, la barra
 * risponde in un colpo d'occhio a "quanto manca", e i due importi restano leggibili per
 * chi il conto lo vuole davvero.
 *
 * La barra non e' decorativa: la quota verde e' `paid / total`. Quando c'e' dello scaduto
 * la parte ancora aperta diventa rossa invece che ambra -- stesso codice colore del resto
 * dei pagamenti, dove il rosso vuol dire "questa riga chiede un'azione".
 */
export function PaymentStatusCell({
  paidAmount,
  pendingAmount,
  overdueAmount = 0,
  totalAmount,
  empty = false,
  compact = false,
}: PaymentStatusCellProps) {
  const { t } = useI18n();
  const money = useMoneyCopy();

  // `Number.isFinite` e non solo `<= 0`: se la RPC non e' ancora allineata questi campi
  // arrivano undefined, e `undefined <= 0` e' false -- la barra finirebbe a NaN%.
  if (empty || !Number.isFinite(totalAmount) || totalAmount <= 0) {
    return <span className="text-gray-400">—</span>;
  }

  // Clamp: un saldo in eccesso non deve far sfondare la barra oltre il 100%.
  const paid = Number.isFinite(paidAmount) ? paidAmount : 0;
  const pending = Number.isFinite(pendingAmount) ? pendingAmount : 0;
  const paidPercent = Math.min(100, Math.max(0, (paid / totalAmount) * 100));
  const hasOverdue = overdueAmount > 0;

  return (
    <div className="flex flex-col items-end gap-1 min-w-[9rem]">
      <span className="text-sm font-semibold text-gray-900 tabular-nums">
        {formatPlanAmount(totalAmount)}
      </span>

      <div
        className={`h-1.5 rounded-full bg-gray-100 overflow-hidden ${
          compact ? "w-3/4" : "w-full"
        }`}
        role="img"
        aria-label={t("dashboard.overview.paymentStatus.barLabel", {
          paid: formatPlanAmount(paid),
          total: formatPlanAmount(totalAmount),
        })}
      >
        <div
          className="h-full rounded-full bg-emerald-500"
          style={{ width: `${paidPercent}%` }}
        />
      </div>

      <div className="flex items-center gap-2 text-xs whitespace-nowrap">
        <span className="text-emerald-700 tabular-nums">
          {formatPlanAmount(paid)} {t(`${money}.paid`).toLowerCase()}
        </span>
        <span
          className={`tabular-nums ${hasOverdue ? "text-red-600 font-medium" : "text-amber-700"}`}
        >
          {formatPlanAmount(pending)}{" "}
          {t("dashboard.overview.paymentStatus.pending").toLowerCase()}
        </span>
      </div>
    </div>
  );
}

export default PaymentStatusCell;
